import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import AppContext from 'Utils/AppContext';
import { listDataSourceInventories, compareInventoryTargets, deriveInventoryForTarget } from 'Secure/api';
import { isEligibleMaskTarget, isSameMaskDestination } from 'DataSources/datasourcesApi';
import { SourcePickerField, RULES } from 'DataSources/SourcePicker';
import TargetSplitReview, { bucketOf } from './TargetSplitReview';
import { apiErrorText } from 'Utils/errorText';

const errorMessage = (e) => apiErrorText(e, String(e));

// Step composer: choose a data source, one of its saved masking inventories, and any number
// of destinations (left), while the right half reviews every chosen destination against the
// inventory as it is picked (POST /masking/jobs/target-compare, one call per newly-added
// destination; a removed destination's card simply disappears). Emits an ARRAY of steps —
// one per destination (fan-out in one add), or a single in-place step when no destination is
// chosen. A step is what a single masking-job start takes:
// (dataSourceId, inventoryId, targetDataSourceId?).
//
// On submit, destinations with schema gaps get their own target_<name> inventory
// (POST /inventories/{id}/derive-for-target) and their step carries that inventory id, so the
// group runner splits them into their own job; profiling / excluded destinations are dropped.
//
// Edit mode: pass `initialSteps` (the steps of one existing block — a fan-out edits as one
// unit) and the composer seeds source/inventory/destinations from them; the caller replaces
// the block with whatever this composer emits.
// `locked` (group run in progress) keeps the pickers usable but blocks the save — the platform
// rejects step edits while a run is active.
function StepComposer({ initialSteps, submitting = false, locked = false, onSubmit, onCancel }) {
  const { connections } = useContext(AppContext);
  const [sourceOption, setSourceOption] = useState(null);
  const [inventoryOption, setInventoryOption] = useState(null);
  // Multi-select: N destinations become N steps in one add (a fan-out); empty = one in-place step.
  const [targetOption, setTargetOption] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [loadingInventories, setLoadingInventories] = useState(false);
  const [error, setError] = useState(null);
  // Live compare state: reports keyed by target id for the CURRENT source+inventory.
  const [reportsById, setReportsById] = useState({});
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [compareTick, setCompareTick] = useState(0); // bumped by Retry / Re-check
  const [preparing, setPreparing] = useState(false); // deriving split inventories on submit
  const editing = !!(initialSteps && initialSteps.length > 0);
  // The seeded step whose inventory still needs re-selecting once the source's inventory
  // list loads. A ref (not state) so the source-change effect below need not depend on it.
  const pendingInitialRef = useRef(null);
  // Mirror of reportsById for the compare effect (which must not re-run on every report).
  const reportsRef = useRef({});
  reportsRef.current = reportsById;
  // Monotonic compare sequence: a result from a superseded source/inventory is dropped.
  const compareSeqRef = useRef(0);

  // Which rows may be a step's source, shown and explained by the shared picker: a masking job
  // is started from them (credentials on file, not platform-managed). No NoSQL masking for
  // dynamo yet (platform rejects with ENGINE_NOT_MASKABLE), and Salesforce is profile +
  // discover only in v1 — either would be a guaranteed-to-fail dead end.
  const sourceRules = useMemo(
    () => [RULES.jobStartable, RULES.notEngine('dynamodb', 'DynamoDB cannot be masked yet'), RULES.notSaas],
    []
  );

  useEffect(() => {
    if (initialSteps && initialSteps.length > 0) {
      // Edit mode: seed from the block being edited. The inventory is re-applied by the
      // source-change effect once the inventory list for the seeded source has loaded.
      const first = initialSteps[0];
      pendingInitialRef.current = first;
      setSourceOption({
        value: first.dataSourceId,
        label: first.dataSourceName || `#${first.dataSourceId}`,
      });
      setTargetOption(
        initialSteps
          .filter((s) => s.targetDataSourceId != null)
          .map((s) => ({
            value: s.targetDataSourceId,
            label: s.targetDataSourceName || `#${s.targetDataSourceId}`,
          }))
      );
    } else {
      pendingInitialRef.current = null;
      setSourceOption(null);
      setTargetOption([]);
    }
    setInventoryOption(null);
    setInventories([]);
    setError(null);
  }, [initialSteps]);

  useEffect(() => {
    if (!sourceOption) {
      setInventories([]);
      setInventoryOption(null);
      // Keep seeded targets while edit-mode seeding is in flight: this effect's first run
      // still sees the pre-seed null sourceOption (state set by the sibling effect lands
      // on the NEXT render) and must not clobber what that effect just set.
      if (!pendingInitialRef.current) setTargetOption([]);
      return;
    }
    let cancelled = false;
    setLoadingInventories(true);
    setInventoryOption(null);
    // Edit-mode seeding lands here too: keep the seeded targets and re-select the seeded
    // inventory once the list arrives. A USER source change still resets both — the chosen
    // source can't be its own destination.
    const initial =
      pendingInitialRef.current && pendingInitialRef.current.dataSourceId === sourceOption.value
        ? pendingInitialRef.current
        : null;
    if (!initial) setTargetOption([]);
    listDataSourceInventories(sourceOption.value)
      .then((list) => {
        if (cancelled) return;
        setInventories(list || []);
        if (initial) {
          const inv = (list || []).find((i) => i.id === initial.inventoryId);
          if (inv) setInventoryOption({ value: inv.id, label: inv.name || `#${inv.id}` });
          pendingInitialRef.current = null;
        }
      })
      .catch(() => {
        if (!cancelled) setInventories([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingInventories(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceOption]);

  const inventoryOptions = useMemo(
    () => inventories.map((inv) => ({ value: inv.id, label: inv.name || `#${inv.id}` })),
    [inventories]
  );

  // The full connection row for the chosen source — needed to compare physical identity
  // against candidate targets (not just the row id).
  const sourceConnection = useMemo(
    () => (connections || []).find((c) => c && c.id === (sourceOption && sourceOption.value)) || null,
    [connections, sourceOption]
  );

  // Shared destination rule (isEligibleMaskTarget): writable DB rows on the same engine,
  // excluding the source row itself, rows resolving to the source's own database+schema,
  // and file rows — other rows on the same host or database (e.g. a different schema) are
  // legitimate targets. The same-engine rule makes mongodb targets available only for
  // mongodb sources. Duplicate rows pointing at the same physical destination are folded
  // to one option — a fan-out must never write the same destination twice (C2D-1134).
  const targetOptions = useMemo(() => {
    if (!sourceConnection) return [];
    const seen = [];
    return (connections || [])
      .filter((c) => isEligibleMaskTarget(sourceConnection, c))
      .filter((c) => {
        if (seen.some((other) => isSameMaskDestination(other, c))) return false;
        seen.push(c);
        return true;
      })
      .map((c) => ({ value: c.id, label: c.name || `#${c.id}` }));
  }, [connections, sourceConnection]);

  // The same rule as the picker's restrictions: eligible first (each refusal says why), then
  // one row per physical destination.
  const targetRules = useMemo(() => {
    if (!sourceConnection) return [];
    const kept = new Set(targetOptions.map((o) => Number(o.value)));
    return [
      RULES.maskTargetFor(sourceConnection),
      RULES.inSet(kept, 'One row per destination', 'Same database and schema as another row already listed'),
    ];
  }, [sourceConnection, targetOptions]);

  const chosenTargets = useMemo(
    () => (Array.isArray(targetOption) ? targetOption : targetOption ? [targetOption] : []),
    [targetOption]
  );
  const targetIdsKey = chosenTargets.map((t) => t.value).join(',');

  // A new source or inventory invalidates every report — the comparison is against the
  // inventory, so the cards refill from scratch for the new pair.
  useEffect(() => {
    compareSeqRef.current += 1;
    // The ref too: the compare effect below runs in this same commit and must not see the
    // previous pair's reports as "already compared".
    reportsRef.current = {};
    setReportsById({});
    setCompareError(null);
    setComparing(false);
  }, [sourceOption, inventoryOption]);

  // Live compare: every chosen destination without a report (for this source+inventory) is
  // sent to target-compare as soon as it is picked. Only the missing ones go — the cards of
  // earlier picks stay put — and a destination removed from the picker just stops rendering.
  useEffect(() => {
    if (!sourceOption || !inventoryOption) return undefined;
    const ids = targetIdsKey ? targetIdsKey.split(',').map(Number) : [];
    const missing = ids.filter((id) => !reportsRef.current[id]);
    if (missing.length === 0) return undefined;
    const seq = compareSeqRef.current;
    setComparing(true);
    setCompareError(null);
    compareInventoryTargets({
      dataSourceId: sourceOption.value,
      inventoryId: inventoryOption.value,
      targetDataSourceIds: missing,
    })
      .then((result) => {
        if (seq !== compareSeqRef.current) return;
        const reports = result && Array.isArray(result.reports) ? result.reports : [];
        setReportsById((prev) => {
          const next = { ...prev };
          reports.forEach((r) => {
            if (r && r.targetDataSourceId != null) next[r.targetDataSourceId] = r;
          });
          return next;
        });
      })
      .catch((e) => {
        if (seq !== compareSeqRef.current) return;
        setCompareError(errorMessage(e));
      })
      .finally(() => {
        if (seq === compareSeqRef.current) setComparing(false);
      });
    return undefined;
  }, [sourceOption, inventoryOption, targetIdsKey, compareTick]);

  const retryCompare = useCallback(() => {
    setCompareError(null);
    setCompareTick((t) => t + 1);
  }, []);
  // A profiling destination re-runs its compare once the user says the Profile is done.
  const recheckTarget = useCallback((id) => {
    setReportsById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setCompareTick((t) => t + 1);
  }, []);

  const reviewCounts = useMemo(() => {
    const c = { shared: 0, split: 0, pending: 0, excluded: 0, unknown: 0 };
    chosenTargets.forEach((t) => {
      const r = reportsById[t.value];
      if (r) c[bucketOf(r)] += 1;
      else c.unknown += 1;
    });
    return c;
  }, [chosenTargets, reportsById]);
  const targetNamesById = useMemo(() => {
    const m = {};
    chosenTargets.forEach((t) => {
      m[t.value] = t.label;
    });
    return m;
  }, [chosenTargets]);
  // Schema of each destination row (its database when no schema is declared), shown grey under
  // the name in the review cards.
  const targetSubtitlesById = useMemo(() => {
    const m = {};
    chosenTargets.forEach((t) => {
      const row = (connections || []).find((c) => c && c.id === t.value);
      const schema = row && (row.schemaName != null ? row.schemaName : row.schema);
      const label = schema != null && String(schema).trim() ? String(schema).trim() : row && row.database;
      if (label) m[t.value] = label;
    });
    return m;
  }, [chosenTargets, connections]);
  const reports = useMemo(
    () => chosenTargets.map((t) => reportsById[t.value]).filter(Boolean),
    [chosenTargets, reportsById]
  );

  const baseStep = () => ({
    dataSourceId: sourceOption.value,
    inventoryId: inventoryOption.value,
    // Display names so the appended rows render before the next refresh lands.
    dataSourceName: sourceOption.label,
    inventoryName: inventoryOption.label,
  });

  const addable = reviewCounts.shared + reviewCounts.split;
  const skipped = reviewCounts.pending + reviewCounts.excluded;
  const busy = comparing || preparing || submitting;
  const canSubmit =
    !!sourceOption &&
    !!inventoryOption &&
    !busy &&
    !locked &&
    !compareError &&
    reviewCounts.unknown === 0 &&
    (chosenTargets.length === 0 || addable > 0);

  /** Emit the reviewed plan: split destinations get a derived inventory, skipped ones are dropped. */
  const handleSubmit = async () => {
    if (!sourceOption || !inventoryOption) {
      setError('Choose a data source and one of its inventories.');
      return;
    }
    const base = baseStep();
    // No destinations = one in-place step; nothing to compare against.
    if (chosenTargets.length === 0) {
      onSubmit([base]);
      return;
    }
    setError(null);
    setPreparing(true);
    try {
      const steps = [];
      for (const t of chosenTargets) {
        const bucket = bucketOf(reportsById[t.value]);
        if (bucket === 'shared') {
          steps.push({ ...base, targetDataSourceId: t.value, targetDataSourceName: t.label });
        } else if (bucket === 'split') {
          const res = await deriveInventoryForTarget(base.inventoryId, t.value);
          const derived = res && res.inventory;
          if (!derived || derived.id == null) {
            // The target changed between compare and derive (e.g. now clean): use the full inventory.
            steps.push({ ...base, targetDataSourceId: t.value, targetDataSourceName: t.label });
          } else {
            steps.push({
              ...base,
              inventoryId: derived.id,
              inventoryName: derived.name,
              targetDataSourceId: t.value,
              targetDataSourceName: t.label,
            });
          }
        }
        // pending / excluded destinations are dropped
      }
      if (steps.length === 0) {
        setError('None of the chosen destinations can run yet. Wait for the fingerprints to finish or pick other destinations.');
        return;
      }
      onSubmit(steps);
    } catch (e) {
      setError(`Could not prepare the split inventories: ${errorMessage(e)}`);
    } finally {
      setPreparing(false);
    }
  };

  const submitLabel = () => {
    if (chosenTargets.length === 0) return editing ? 'Save' : 'Add step';
    if (reviewCounts.unknown > 0 || compareError) {
      return `${editing ? 'Save' : 'Add'} ${chosenTargets.length} step${chosenTargets.length === 1 ? '' : 's'}`;
    }
    return (
      `${editing ? 'Save' : 'Add'} ${addable} step${addable === 1 ? '' : 's'}` +
      (reviewCounts.split > 0 ? ` (${reviewCounts.split} separate)` : '') +
      (skipped > 0 ? `, ${skipped} skipped` : '')
    );
  };

  const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 2000 }) };

  return (
    <div className='d-flex flex-wrap align-items-stretch' style={{ gap: 16 }} data-testid='step-composer'>
      <div className='sg-card p-3 d-flex flex-column' style={{ flex: '1 1 360px', minWidth: 0 }}>
        {error && <Alert variant='danger'>{error}</Alert>}
        <Form.Group>
          <Form.Label>Data source</Form.Label>
          <SourcePickerField
            value={sourceOption ? sourceOption.value : null}
            names={sourceOption ? { [sourceOption.value]: sourceOption.label } : {}}
            onChange={(id, row) => setSourceOption(row ? { value: row.id, label: row.name || `#${row.id}` } : null)}
            restrict={sourceRules}
            placeholder='No data source chosen'
            dialogTitle='Choose the data source to mask'
            testId='step-source'
          />
        </Form.Group>
        <Form.Group className='mb-1'>
          <Form.Label>
            Masking inventory {loadingInventories && <Spinner animation='border' size='sm' />}
          </Form.Label>
          <Select
            options={inventoryOptions}
            value={inventoryOption}
            onChange={setInventoryOption}
            isDisabled={!sourceOption || loadingInventories}
            placeholder={sourceOption ? 'Select an inventory…' : 'Choose a data source first'}
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
        </Form.Group>
        {sourceOption && !loadingInventories && inventoryOptions.length === 0 && (
          <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
            This source has no saved masking inventories yet. Create one from its masking
            page first.
          </div>
        )}
        <Form.Group className='mb-1 mt-3'>
          <Form.Label>Destinations (optional: in place when empty, several = fan-out)</Form.Label>
          <SourcePickerField
            selection='multiple'
            value={chosenTargets.map((t) => t.value)}
            names={Object.fromEntries(chosenTargets.map((t) => [t.value, t.label]))}
            onChange={(ids) => setTargetOption(ids.map((id) => {
              const prior = chosenTargets.find((t) => String(t.value) === String(id));
              const row = (connections || []).find((c) => String(c.id) === String(id));
              return prior || { value: row ? row.id : id, label: (row && row.name) || `#${id}` };
            }))}
            restrict={targetRules}
            disabled={!sourceOption}
            emptyLabel={sourceOption ? 'In place, no separate destination' : 'Choose a data source first'}
            dialogTitle={sourceOption ? `Destinations for ${sourceOption.label}` : 'Destinations'}
            dialogSubtitle='Each destination becomes its own step; several make a fan-out.'
            verb='Use as destinations'
            testId='step-targets'
          />
        </Form.Group>
        {chosenTargets.length > 0 && !inventoryOption && (
          <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
            Pick an inventory to compare each destination against it.
          </div>
        )}
        <div className='d-flex justify-content-end mt-auto pt-3' style={{ gap: 8 }}>
          <Button variant='outline-secondary' size='sm' onClick={onCancel} disabled={preparing || submitting}>
            Cancel
          </Button>
          <Button
            variant='primary'
            size='sm'
            data-testid='job-group-add-step-confirm'
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {(preparing || submitting) && <Spinner animation='border' size='sm' className='mr-1' />}
            {submitLabel()}
          </Button>
        </div>
      </div>
      <div
        className='sg-card p-3 text-left'
        style={{ flex: '1 1 360px', minWidth: 0, alignSelf: 'flex-start' }}
        data-testid='step-composer-review'
      >
        {chosenTargets.length === 0 ? (
          <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
            <strong style={{ color: 'var(--ink-700)' }}>Destination review</strong>
            <div className='mt-1'>
              {inventoryOption
                ? 'Add destinations to see how each one compares with the inventory: missing tables or columns, ' +
                  'duplicate-key conflicts, and whether it joins the fan-out job or runs on its own.'
                : 'With no destination the inventory is masked in place on the source. Add destinations to compare ' +
                  'each one with the chosen inventory here.'}
            </div>
          </div>
        ) : !inventoryOption ? (
          <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
            <strong style={{ color: 'var(--ink-700)' }}>Destination review</strong>
            <div className='mt-1'>Choose a masking inventory to compare these destinations against.</div>
          </div>
        ) : (
          <TargetSplitReview
            reports={reports}
            targets={chosenTargets}
            targetNames={targetNamesById}
            targetSubtitles={targetSubtitlesById}
            comparing={comparing}
            compareError={compareError}
            onRetry={retryCompare}
            onRecheck={recheckTarget}
          />
        )}
      </div>
    </div>
  );
}

export default StepComposer;
